import { API_BASE_URL } from '../config';
import { auth } from './auth';

// 行为类型
export type BehaviorAction =
  | 'start_interview'
  | 'complete_interview'
  | 'view_report'
  | 'share_report'
  | 'daily_active'
  | 'page_view'
  | 'export_resume'
  | 'edit_resume';

// 平台类型
export type Platform = 'pc' | 'mobile' | 'miniprogram';

// 行为日志参数
export interface BehaviorLogParams {
  action: BehaviorAction;
  interviewId?: number;
  metadata?: Record<string, any>;
  platform?: Platform;
}

/**
 * 记录用户行为日志
 * 用于追踪用户在网站上的行为，支持留存率、活跃度等分析
 *
 * @param params 行为日志参数
 * @returns 是否记录成功
 */
export async function recordBehavior(
  params: BehaviorLogParams,
): Promise<boolean> {
  try {
    // 检查用户是否已登录
    if (!auth.isAuthenticated()) {
      console.log('用户未登录，跳过行为记录');
      return false;
    }

    const token = auth.getToken();
    if (!token) {
      return false;
    }

    const response = await fetch(`${API_BASE_URL}/api/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: params.action,
        interviewId: params.interviewId,
        metadata: params.metadata,
        platform: params.platform || 'pc',
      }),
    });

    if (!response.ok) {
      console.error('记录行为日志失败:', response.status);
      return false;
    }

    return true;
  } catch (error) {
    // 记录失败不影响主流程
    console.error('记录行为日志异常:', error);
    return false;
  }
}

/**
 * 记录面试开始行为
 *
 * @param interviewConfig 面试配置信息
 * @returns 是否记录成功
 */
export async function trackInterviewStart(interviewConfig: {
  jobType: string;
  jobLevel: string;
  interviewType: string;
  interviewerId: string;
}): Promise<boolean> {
  return recordBehavior({
    action: 'start_interview',
    metadata: {
      jobType: interviewConfig.jobType,
      jobLevel: interviewConfig.jobLevel,
      interviewType: interviewConfig.interviewType,
      interviewerId: interviewConfig.interviewerId,
      timestamp: Date.now(),
    },
  });
}

/**
 * 记录面试完成行为
 *
 * @param interviewId 面试记录ID
 * @param reportData 报告数据
 * @returns 是否记录成功
 */
export async function trackInterviewComplete(
  interviewId: number,
  reportData?: {
    score?: number;
    duration?: number; // 面试时长（秒）
    rounds?: number; // 对话轮数
  },
): Promise<boolean> {
  return recordBehavior({
    action: 'complete_interview',
    interviewId,
    metadata: {
      ...reportData,
      timestamp: Date.now(),
    },
  });
}

/**
 * 记录查看报告行为
 *
 * @param interviewId 面试记录ID
 * @returns 是否记录成功
 */
export async function trackViewReport(interviewId: number): Promise<boolean> {
  return recordBehavior({
    action: 'view_report',
    interviewId,
    metadata: {
      timestamp: Date.now(),
    },
  });
}

/**
 * 记录分享报告行为
 *
 * @param interviewId 面试记录ID
 * @param shareMethod 分享方式（如：wechat, link, image）
 * @returns 是否记录成功
 */
export async function trackShareReport(
  interviewId: number,
  shareMethod?: string,
): Promise<boolean> {
  return recordBehavior({
    action: 'share_report',
    interviewId,
    metadata: {
      shareMethod,
      timestamp: Date.now(),
    },
  });
}

/**
 * 记录每日活跃行为
 * 建议在用户打开应用或进行重要操作时调用
 *
 * @returns 是否记录成功
 */
export async function trackDailyActive(): Promise<boolean> {
  return recordBehavior({
    action: 'daily_active',
    metadata: {
      timestamp: Date.now(),
      url: window.location.pathname,
    },
  });
}

/**
 * 防抖函数，用于限制频繁的行为记录
 */
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// 用于防止重复记录每日活跃
const debouncedTrackDailyActive = debounce(trackDailyActive, 900000); // 15分钟内只记录一次

// 用于防止短时间内重复上报页面浏览
let lastTrackedUrl: string | null = null;
let lastTrackedTime: number = 0;
const TRACK_COOLDOWN = 1000; // 1秒内同一URL不重复上报

/**
 * 记录页面浏览行为
 * 用于PV/UV统计分析
 *
 * @param pagePath 页面路径，默认为当前路径
 * @returns 是否记录成功
 */
export async function trackPageView(pagePath?: string): Promise<boolean> {
  const currentUrl = pagePath || window.location.pathname;
  const currentTime = Date.now();

  // 检查是否在冷却时间内重复上报同一URL
  if (
    lastTrackedUrl === currentUrl &&
    currentTime - lastTrackedTime < TRACK_COOLDOWN
  ) {
    return false;
  }

  // 更新上次上报记录
  lastTrackedUrl = currentUrl;
  lastTrackedTime = currentTime;

  return recordBehavior({
    action: 'page_view',
    metadata: {
      timestamp: currentTime,
      url: currentUrl,
      referrer: document.referrer || null,
    },
  });
}

// 用于防止重复初始化
let isTrackingInitialized = false;

/**
 * 记录导出简历行为
 *
 * @param template 使用的简历模板
 * @param metadata 其他元数据
 * @returns 是否记录成功
 */
export async function trackExportResume(
  template?: string,
  metadata?: Record<string, any>,
): Promise<boolean> {
  return recordBehavior({
    action: 'export_resume',
    metadata: {
      template,
      timestamp: Date.now(),
      ...metadata,
    },
  });
}

/**
 * 记录编辑简历行为
 *
 * @param fileType 文件类型（如 pdf, docx, txt, md）
 * @param metadata 其他元数据
 * @returns 是否记录成功
 */
export async function trackEditResume(
  fileType?: string,
  metadata?: Record<string, any>,
): Promise<boolean> {
  return recordBehavior({
    action: 'edit_resume',
    metadata: {
      fileType,
      timestamp: Date.now(),
      ...metadata,
    },
  });
}

/**
 * 初始化行为追踪
 * 在应用启动时调用，自动记录每日活跃和页面浏览
 */
export function initBehaviorTracking(): void {
  // 防止重复初始化
  if (isTrackingInitialized) {
    return;
  }
  isTrackingInitialized = true;

  // 记录每日活跃（带防抖）
  debouncedTrackDailyActive();

  // 记录页面浏览
  trackPageView();

  // 监听页面可见性变化，当用户重新打开页面时记录活跃
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      debouncedTrackDailyActive();
    }
  });

  // 监听路由变化，记录页面浏览
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    trackPageView();
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    trackPageView();
  };

  window.addEventListener('popstate', () => {
    trackPageView();
  });
}
